// PWA — 任务轮询状态 Hook（Req 7.5, 8.1, 8.2 / 任务 9.5）
//
// 职责：
//  - 给定 jobId，按固定间隔轮询 `GET /jobs/{id}`（authedFetch 自动带 JWT），
//    直至任务进入终止态（success / failed）后停止（Req 7.5）。
//  - success 时响应体附带 presigned 图片 URL 列表，供组件直取 S3 原图显示
//    （Req 8.1, 8.2）；failed 时透传可读原因。
//
// 与 usePower 一致的挂载 / 取消守卫：
//  - jobId 变化即启动新一轮轮询并令旧轮询失效（flowRef 令牌）；
//  - 组件卸载后不再 setState，避免竞态告警。
//
// 全部为出站请求，符合零入站架构。为后续任务 9.6（保存图片 / 取图失败重试）
// 预留 refresh()（手动重取一次）。

import { useCallback, useEffect, useRef, useState } from "react";
import { isTerminal, type JobResponse } from "./jobView.js";

/** authedFetch 的最小签名（与 useAuth / usePower / useSubmit 暴露的一致）。 */
export type AuthedFetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface UseJobPollingOptions {
  /** 带认证的 fetch（自动附带 Bearer token）。 */
  authedFetch: AuthedFetch;
  /** 要轮询的 jobId；为 null 时不发起任何请求。 */
  jobId: string | null;
  /** 轮询间隔（ms），默认 3000。 */
  pollIntervalMs?: number;
}

export interface UseJobPollingResult {
  /** 最近一次 `GET /jobs/{id}` 的响应；尚未取到时为 null。 */
  job: JobResponse | null;
  /** 是否正在轮询（已发起且未到终止态）。 */
  polling: boolean;
  /** 最近一次错误信息。 */
  error: string | null;
  /** 手动重取一次当前 jobId 的状态（用于任务 9.6 的取图失败重试）。 */
  refresh: () => void;
}

const DEFAULT_POLL_MS = 3000;

export function useJobPolling(
  opts: UseJobPollingOptions,
): UseJobPollingResult {
  const { authedFetch, jobId, pollIntervalMs = DEFAULT_POLL_MS } = opts;

  const [job, setJob] = useState<JobResponse | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 挂载标记：卸载后不再 setState。
  const mountedRef = useRef(true);
  // 轮询流取消令牌：递增即令旧流失效（jobId 变化 / 卸载）。
  const flowRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      flowRef.current += 1;
    };
  }, []);

  // GET /jobs/{id} → JobResponse
  const fetchJob = useCallback(
    async (id: string): Promise<JobResponse | null> => {
      const res = await authedFetch(`/jobs/${encodeURIComponent(id)}`);
      if (!res.ok) {
        throw new Error(`获取任务状态失败（${res.status}）`);
      }
      return (await res.json()) as JobResponse;
    },
    [authedFetch],
  );

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  // 启动一轮轮询：读取 jobId 状态直到终止态。
  const runPolling = useCallback(
    (id: string) => {
      const token = ++flowRef.current;
      const active = () => mountedRef.current && flowRef.current === token;

      const loop = async () => {
        setPolling(true);
        setError(null);
        try {
          // eslint-disable-next-line no-constant-condition
          while (active()) {
            const body = await fetchJob(id);
            if (!active()) return;
            if (!body) break;
            setJob(body);
            if (isTerminal(body.status)) {
              break; // success / failed → 停止轮询（Req 7.5）
            }
            await sleep(pollIntervalMs);
          }
        } catch (err: unknown) {
          if (active()) {
            setError(err instanceof Error ? err.message : String(err));
          }
        } finally {
          if (active()) setPolling(false);
        }
      };

      void loop();
    },
    [fetchJob, pollIntervalMs],
  );

  // jobId 变化：复位并对新任务启动轮询；为 null 时清空。
  useEffect(() => {
    // 令旧轮询失效。
    flowRef.current += 1;
    setJob(null);
    setError(null);
    if (!jobId) {
      setPolling(false);
      return;
    }
    runPolling(jobId);
  }, [jobId, runPolling]);

  // 手动重取一次（不改变轮询流；用于终止态后的重试探测）。
  const refresh = useCallback(() => {
    if (!jobId) return;
    runPolling(jobId);
  }, [jobId, runPolling]);

  return { job, polling, error, refresh };
}
